import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'

// Synchronous pre-check: if the idle session has expired (e.g. browser was
// closed before the in-app timer fired), clear Supabase auth tokens NOW so
// Supabase doesn't spend time refreshing a session we'd immediately sign out
// of anyway. This prevents the "loading screen before login" on first open.
;(function clearExpiredIdleSession() {
  const IDLE_TIMEOUT_MS = 3 * 60 * 60 * 1000 // must stay in sync with INTERNAL_TIMEOUT_MS in App.jsx
  try {
    const stored = localStorage.getItem('idle_internal')
    if (stored && Date.now() - Number(stored) > IDLE_TIMEOUT_MS) {
      localStorage.removeItem('idle_internal')
      Object.keys(localStorage)
        .filter(k => k.startsWith('sb-') && k.includes('-auth'))
        .forEach(k => localStorage.removeItem(k))
      localStorage.removeItem('profile_cache')
    }
  } catch {}
})()

// When a lazy-loaded chunk can't be fetched (stale index.html after a new
// deploy), reload once to pick up the fresh index.html + new chunk hashes.
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason?.message?.includes('Failed to fetch dynamically imported module') ||
      e.reason?.message?.includes('Importing a module script failed')) {
    const key = 'chunk_reload_at'
    const last = Number(sessionStorage.getItem(key) || 0)
    if (Date.now() - last > 10_000) {
      sessionStorage.setItem(key, String(Date.now()))
      window.location.reload()
    }
  }
})

Sentry.init({
  dsn: 'https://a90f59145a52b83f9cdea4d3d9c668b2@o4512267300700160.ingest.us.sentry.io/4512267342708736',
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
  ],
  // Capture a full session replay whenever an error occurs — very useful for debugging
  replaysOnErrorSampleRate: 1.0,
  // No random background session recording (privacy)
  replaysSessionSampleRate: 0,
  // Low performance tracing — enough to see slow operations without noise
  tracesSampleRate: 0.05,
})

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
